import {FC, useCallback, useEffect, useState} from 'react'
import {
  api,
  NotifyChannel,
  WaitlistEntry,
  WaitlistInterest,
  WaitlistList,
  WaitlistStatus,
} from '../../services/api'
import {toast} from '../../utils/toast'

const card = {background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'}
const input = {width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc', outline: 'none', boxSizing: 'border-box' as const}
const lbl = {fontSize: 13, fontWeight: 600 as const, color: '#374151', display: 'block' as const, marginBottom: 6}

const INTEREST: Record<WaitlistInterest, {label: string; bg: string; color: string}> = {
  customer: {label: 'Needs car care', bg: '#eff6ff', color: '#2563eb'},
  driver: {label: 'Wants to drive', bg: '#ecfdf5', color: '#059669'},
  shop: {label: 'Runs a shop', bg: '#f5f3ff', color: '#7c3aed'},
  home_service: {label: 'Home service', bg: '#fff7ed', color: '#c2410c'},
}

const STATUS: Record<
  WaitlistStatus,
  {label: string; bg: string; color: string}
> = {
  verified: {label: 'Verified', bg: '#ecfdf5', color: '#047857'},
  pending: {label: 'Unverified', bg: '#f1f5f9', color: '#475569'},
  suspicious: {label: 'Suspicious', bg: '#fffbeb', color: '#b45309'},
  rejected: {label: 'Rejected', bg: '#fef2f2', color: '#b91c1c'},
}

/**
 * Plain-English reasons. The server stores slugs so they stay stable and
 * searchable; nobody reading this screen should have to know what
 * 'ip_burst' means.
 */
const FLAG_LABEL: Record<string, string> = {
  disposable_email: 'Throwaway email address',
  random_looking_email: 'Address looks machine-generated',
  invalid_phone: 'Phone number is not a real number',
  ip_burst: 'Several signups from this IP in one hour',
  captcha_unavailable: 'Bot check could not be run — not necessarily a bot',
  blocked_ip: 'IP is blocked',
  blocked_email: 'Email is blocked',
  blocked_email_domain: 'Email domain is blocked',
  blocked_phone: 'Phone number is blocked',
}

const WaitlistPage: FC = () => {
  const [data, setData] = useState<WaitlistList | null>(null)
  const [interest, setInterest] = useState<WaitlistInterest | ''>('')
  const [notified, setNotified] = useState<'' | 'true' | 'false'>('')
  const [status, setStatus] = useState<WaitlistStatus | ''>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  /**
   * Which row's action menu is open, and where to draw it.
   *
   * The coordinates are kept here rather than positioning the menu inside the
   * cell, because the table sits in a horizontally scrolling container: an
   * absolutely positioned menu would be clipped by it and half the options
   * would be unreachable. Fixed positioning from the button's own rectangle
   * escapes that.
   */
  const [menu, setMenu] = useState<{id: string; x: number; y: number} | null>(
    null,
  )

  // Anything that moves the page moves the button out from under the menu,
  // so close rather than leave it floating somewhere wrong.
  useEffect(() => {
    if (!menu) return

    const close = () => setMenu(null)
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()

    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', close)
    }
  }, [menu])

  // Which rows the announcement goes to. Empty means "everyone the filters are
  // not hiding", which is the common case, so it is not a required step.
  const [picked, setPicked] = useState<string[]>([])

  const [composing, setComposing] = useState(false)
  const [channels, setChannels] = useState<NotifyChannel[]>(['email'])
  const [subject, setSubject] = useState('Qwiksurv is live')
  const [message, setMessage] = useState(
    "We're live. Download Qwiksurv and book your first service — pickup and return, or at your door.\n\nGet the app: {link}",
  )
  const [audience, setAudience] = useState<WaitlistInterest | ''>('')
  const [resend, setResend] = useState(false)
  const [sending, setSending] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(
        await api.getWaitlist({
          interest: interest || undefined,
          notified: notified === '' ? undefined : notified === 'true',
          status: status || undefined,
        }),
      )
      setPicked([])
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load the waitlist')
    } finally {
      setLoading(false)
    }
  }, [interest, notified, status])

  /** An admin's verdict on one row, reloading so the counts follow it. */
  const judge = async (id: string, next: WaitlistStatus) => {
    try {
      await api.setWaitlistStatus(id, next)
      toast(`Marked as ${STATUS[next].label.toLowerCase()}`)
      reload()
    } catch (e: any) {
      toast(e?.message ?? 'Could not change the status', 'error')
    }
  }

  /**
   * Blocks whatever identified this signup and rejects the row. The email is
   * the one detail always present — an IP can be shared by a whole building
   * and a phone number is optional, so blocking those is left as a deliberate
   * choice on the Blocked list rather than something done in one click here.
   */
  const block = async (entry: WaitlistEntry) => {
    if (!window.confirm(`Block ${entry.email} from signing up again?`)) return
    try {
      await api.addWaitlistBlock({
        type: 'email',
        value: entry.email,
        reason: 'Blocked from the waitlist screen',
      })
      await api.setWaitlistStatus(entry.id, 'rejected')
      toast('Blocked')
      reload()
    } catch (e: any) {
      toast(e?.message ?? 'Could not block that address', 'error')
    }
  }
  useEffect(() => { reload() }, [reload])

  const rows = data?.items ?? []
  const allPicked = rows.length > 0 && picked.length === rows.length

  const toggle = (id: string) =>
    setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]))

  const send = async () => {
    if (!subject.trim() || !message.trim()) {
      toast('Give the announcement a subject and a message', 'error')
      return
    }
    setSending(true)
    try {
      const result = await api.notifyWaitlist({
        ids: picked.length ? picked : undefined,
        interest: picked.length ? undefined : audience || undefined,
        channels,
        subject: subject.trim(),
        message: message.trim(),
        resend,
      })
      const parts = [`${result.emailed} emailed`]
      if (channels.includes('sms')) parts.push(`${result.texted} texted`)
      if (result.failed.length) parts.push(`${result.failed.length} failed`)
      toast(parts.join(' · '), result.failed.length ? 'error' : 'success')
      // Said plainly rather than buried in a server log: a send that went out
      // without a download link is the failure this feature exists to prevent.
      if (result.missingLinks?.length) {
        toast(
          `Sent without a link for: ${result.missingLinks.join(', ')} — set the app URLs on the server.`,
          'error',
        )
      }
      setComposing(false)
      reload()
    } catch (e: any) {
      toast(e?.message ?? 'Could not send', 'error')
    } finally {
      setSending(false)
    }
  }

  const stat = (label: string, value: number, tone?: string) => (
    <div style={{...card, padding: '16px 20px', flex: '1 1 150px'}}>
      <div style={{fontSize: 26, fontWeight: 800, color: tone ?? '#0f172a', lineHeight: 1.2}}>{value}</div>
      <div style={{fontSize: 13, color: '#64748b'}}>{label}</div>
    </div>
  )

  return (
    <div>
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Waitlist</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>
          People waiting on launch, and which side of the marketplace they came for — notify them by email, SMS, or both
        </p>
      </div>

      <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16}}>
        {stat('Verified leads', data?.total ?? 0, '#047857')}
        {stat('Need car care', data?.customers ?? 0, '#2563eb')}
        {stat('Want to drive', data?.drivers ?? 0, '#059669')}
        {stat('Run a shop', data?.shops ?? 0, '#7c3aed')}
        {stat('Home service', data?.homeService ?? 0, '#c2410c')}
        {stat('Not yet told', data?.pending ?? 0, '#b45309')}
      </div>

      {/*
        The figures above count verified signups only. This line accounts for
        the difference, so the gap between "people who submitted the form" and
        "people we know exist" is visible rather than quietly dropped — and so
        nobody wonders why the headline number fell.
      */}
      {data && data.submissions > data.total && (
        <div style={{...card, padding: '12px 20px', marginBottom: 16, fontSize: 13, color: '#475569', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center'}}>
          <span><strong style={{color: '#0f172a'}}>{data.submissions}</strong> submitted in total —</span>
          {(['pending', 'suspicious', 'rejected'] as WaitlistStatus[])
            .filter(s => (data.byStatus?.[s] ?? 0) > 0)
            .map(s => (
              <button key={s} onClick={() => setStatus(s)}
                style={{padding: '3px 10px', borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: STATUS[s].bg, color: STATUS[s].color}}>
                {data.byStatus[s]} {STATUS[s].label.toLowerCase()}
              </button>
            ))}
          <span style={{color: '#94a3b8'}}>Only verified signups are counted above.</span>
        </div>
      )}

      <div style={{...card, padding: '16px 20px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap'}}>
        <select value={interest} onChange={e => setInterest(e.target.value as WaitlistInterest | '')}
          style={{...input, width: 'auto', minWidth: 180, flex: '0 1 auto'}}>
          <option value=''>Everyone</option>
          <option value='customer'>Needs car care</option>
          <option value='driver'>Wants to drive</option>
          <option value='shop'>Runs a shop</option>
          <option value='home_service'>Home service</option>
        </select>
        <select value={notified} onChange={e => setNotified(e.target.value as '' | 'true' | 'false')}
          style={{...input, width: 'auto', minWidth: 180, flex: '0 1 auto'}}>
          <option value=''>Told and not told</option>
          <option value='false'>Not yet told</option>
          <option value='true'>Already told</option>
        </select>
        <select value={status} onChange={e => setStatus(e.target.value as WaitlistStatus | '')}
          style={{...input, width: 'auto', minWidth: 180, flex: '0 1 auto'}}>
          <option value=''>Any status</option>
          <option value='verified'>Verified</option>
          <option value='pending'>Unverified</option>
          <option value='suspicious'>Suspicious</option>
          <option value='rejected'>Rejected</option>
        </select>
        <button onClick={reload} disabled={loading}
          style={{padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer'}}>
          Refresh
        </button>
        <div style={{flex: 1}} />
        <button onClick={() => { setAudience(interest); setComposing(true) }} disabled={!rows.length}
          style={{padding: '10px 18px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: rows.length ? '#3b82f6' : '#cbd5e1', cursor: rows.length ? 'pointer' : 'not-allowed'}}>
          {picked.length ? `Notify ${picked.length} selected` : 'Notify the list'}
        </button>
      </div>

      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16}}>
          {error}
        </div>
      )}

      <div style={{...card, overflow: 'hidden'}}>
        {loading ? (
          <div style={{padding: '60px 0', textAlign: 'center'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  <th style={{padding: '11px 20px', width: 44}}>
                    <input type='checkbox' checked={allPicked}
                      onChange={e => setPicked(e.target.checked ? rows.map(r => r.id) : [])} />
                  </th>
                  {['EMAIL', 'STATUS', 'PHONE', 'INTERESTED AS', 'SIGNED UP FROM', 'JOINED', 'NOTIFIED', 'ACTION'].map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r: WaitlistEntry, i) => {
                  const tag = INTEREST[r.interest]
                  return (
                    <tr key={r.id} style={{borderBottom: i < rows.length - 1 ? '1px solid #f1f5f9' : 'none'}}>
                      <td style={{padding: '14px 20px'}}>
                        <input type='checkbox' checked={picked.includes(r.id)} onChange={() => toggle(r.id)} />
                      </td>
                      <td style={{padding: '14px 20px', fontWeight: 600, color: '#0f172a'}}>{r.email}</td>
                      <td style={{padding: '14px 20px'}}>
                        <span style={{padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', background: STATUS[r.status]?.bg, color: STATUS[r.status]?.color}}>
                          {STATUS[r.status]?.label ?? r.status}
                        </span>
                        {/* Why it was flagged, in words — the slug alone tells
                            a reader nothing and makes the flag unactionable. */}
                        {r.flagReasons?.length ? (
                          <div style={{marginTop: 4, fontSize: 11, color: '#b45309', maxWidth: 220}}>
                            {r.flagReasons.map(f => FLAG_LABEL[f] ?? f).join(' · ')}
                          </div>
                        ) : null}
                      </td>
                      <td style={{padding: '14px 20px', color: r.phone ? '#64748b' : '#cbd5e1'}}>
                        {r.phone ?? 'no number'}
                      </td>
                      <td style={{padding: '14px 20px'}}>
                        <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: tag.bg, color: tag.color}}>
                          {tag.label}
                        </span>
                      </td>
                      {/* Where it came from. The IP is what makes a burst of
                          signups recognisable as one source, and what an admin
                          blocks; the browser and OS are the corroborating
                          detail when a dozen rows share all three. */}
                      <td style={{padding: '14px 20px', fontSize: 13, color: '#64748b'}}>
                        {r.signupIp ? (
                          <>
                            <div style={{fontVariantNumeric: 'tabular-nums'}}>{r.signupIp}</div>
                            {r.device ? (
                              <div style={{fontSize: 11, color: '#94a3b8'}}>
                                {[r.device.browser, r.device.os, r.device.type].filter(Boolean).join(' · ')}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <span style={{color: '#cbd5e1'}}>not recorded</span>
                        )}
                      </td>
                      <td style={{padding: '14px 20px', color: '#64748b'}}>
                        {new Date(r.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{padding: '14px 20px', fontSize: 13}}>
                        {r.notifiedAt ? (
                          <span style={{color: '#16a34a'}}>
                            {new Date(r.notifiedAt).toLocaleDateString()}
                            {r.notifiedVia?.length ? ` · ${r.notifiedVia.join(' + ')}` : ''}
                          </span>
                        ) : (
                          <span style={{color: '#b45309'}}>Not yet</span>
                        )}
                      </td>
                      <td style={{padding: '14px 20px', textAlign: 'right', whiteSpace: 'nowrap'}}>
                        <button
                          aria-label={`Actions for ${r.email}`}
                          aria-haspopup='menu'
                          aria-expanded={menu?.id === r.id}
                          // Stops the document-level listener that closes the
                          // menu from firing on the very click that opens it.
                          onMouseDown={e => e.stopPropagation()}
                          onClick={e => {
                            if (menu?.id === r.id) return setMenu(null)
                            const b = (e.currentTarget as HTMLElement).getBoundingClientRect()
                            // Anchored to the button's right edge so the menu
                            // grows inwards and cannot run off the screen.
                            setMenu({id: r.id, x: b.right, y: b.bottom + 4})
                          }}
                          style={{width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 18, lineHeight: 1, color: '#475569', background: menu?.id === r.id ? '#f1f5f9' : '#fff', cursor: 'pointer'}}>
                          ⋮
                        </button>
                      </td>
                    </tr>
                  )
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={9} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>Nobody here yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/*
        The row action menu. Rendered here rather than inside the cell so the
        table's horizontal scroll container cannot clip it, and positioned from
        the trigger's own rectangle.
      */}
      {menu && (() => {
        const r = rows.find(x => x.id === menu.id)
        if (!r) return null

        const item = (
          label: string,
          onClick: () => void,
          tone: string,
        ) => (
          <button
            role='menuitem'
            onClick={() => { setMenu(null); onClick() }}
            style={{display: 'block', width: '100%', padding: '9px 14px', border: 'none', background: 'none', textAlign: 'left', fontSize: 14, color: tone, cursor: 'pointer'}}
            onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
            {label}
          </button>
        )

        return (
          <div
            role='menu'
            onMouseDown={e => e.stopPropagation()}
            style={{position: 'fixed', top: menu.y, left: menu.x, transform: 'translateX(-100%)', minWidth: 170, ...card, padding: '6px 0', zIndex: 300, boxShadow: '0 10px 30px rgba(15,23,42,0.14)'}}>
            {/* Approve is offered on anything not already verified, because the
                flags are guesses and clearing a wrongly flagged customer has to
                be quick. */}
            {r.status !== 'verified' &&
              item('Approve', () => judge(r.id, 'verified'), '#047857')}
            {r.status !== 'rejected' &&
              item('Reject', () => judge(r.id, 'rejected'), '#475569')}
            {item('Block', () => block(r), '#c2410c')}

            <div style={{height: 1, background: '#f1f5f9', margin: '6px 0'}} />

            {item(
              'Remove',
              async () => {
                if (!window.confirm(`Remove ${r.email} from the waitlist?`)) return
                try {
                  await api.deleteWaitlistEntry(r.id)
                  toast('Removed')
                  reload()
                } catch (e: any) {
                  toast(e?.message ?? 'Failed', 'error')
                }
              },
              '#ef4444',
            )}
          </div>
        )
      })()}

      {composing && (
        <div onClick={() => setComposing(false)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()} style={{...card, width: 560, maxHeight: '90vh', overflowY: 'auto'}}>
            <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <h3 style={{fontSize: 18, fontWeight: 700, margin: 0}}>Notify the waitlist</h3>
              <button onClick={() => setComposing(false)} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 20}}>✕</button>
            </div>

            <div style={{padding: 24, display: 'flex', flexDirection: 'column', gap: 16}}>
              <div style={{padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#475569'}}>
                {picked.length
                  ? `Going to the ${picked.length} selected.`
                  : (() => {
                      const who = audience ? INTEREST[audience].label.toLowerCase() : 'everyone'
                      if (resend) return `Going to ${who} on the list, including those already told.`
                      const n = audience
                        ? data?.pendingByInterest?.[audience] ?? 0
                        : data?.pending ?? 0
                      return `Going to ${who} not yet told (${n}).`
                    })()}
                {channels.includes('sms') && ` Only ${data?.withPhone ?? 0} have a number, so the rest get email only.`}
              </div>

              {!picked.length && (
                <div>
                  <label style={lbl}>Send to</label>
                  <select value={audience} onChange={e => setAudience(e.target.value as WaitlistInterest | '')} style={input}>
                    <option value=''>Everyone on the list</option>
                    {(Object.keys(INTEREST) as WaitlistInterest[]).map(k => (
                      <option key={k} value={k}>
                        {INTEREST[k].label} ({data?.pendingByInterest?.[k] ?? 0} waiting)
                      </option>
                    ))}
                  </select>
                  <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>
                    Each group gets the download link for the app it actually uses.
                  </div>
                </div>
              )}

              <div>
                <label style={lbl}>Send by</label>
                <div style={{display: 'flex', gap: 16}}>
                  {(['email', 'sms'] as NotifyChannel[]).map(c => (
                    <label key={c} style={{display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151'}}>
                      <input type='checkbox' checked={channels.includes(c)}
                        onChange={e => setChannels(prev => e.target.checked ? [...prev, c] : prev.filter(x => x !== c))} />
                      {c === 'email' ? 'Email' : 'SMS'}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={lbl}>Subject <span style={{color: '#94a3b8', fontWeight: 400}}>(email only)</span></label>
                <input value={subject} onChange={e => setSubject(e.target.value)} style={input} />
              </div>

              <div>
                <label style={lbl}>Message</label>
                <textarea rows={5} value={message} onChange={e => setMessage(e.target.value)}
                  style={{...input, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5}} />
                <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>
                  Sent as-is to both channels, so keep it short enough to read as a text.
                </div>
                {/* The two apps are the reason this exists: a customer and a
                    shop owner download different things, so one hard-coded
                    link would send half the list to the wrong store. */}
                <div style={{marginTop: 8, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 12, color: '#1e40af', lineHeight: 1.5}}>
                  <strong>{'{link}'}</strong> becomes the right app for each person —
                  the customer app for “needs car care”, the partner app for drivers,
                  shops and home service. Leave it out and the link is added at the end.
                </div>
              </div>

              {!picked.length && (
                <label style={{display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151'}}>
                  <input type='checkbox' checked={resend} onChange={e => setResend(e.target.checked)} />
                  Include people who have already been told
                </label>
              )}
            </div>

            <div style={{padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10}}>
              <button onClick={() => setComposing(false)} disabled={sending}
                style={{padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                Cancel
              </button>
              <button onClick={send} disabled={sending || !channels.length}
                style={{padding: '9px 20px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 600, color: '#fff', background: channels.length ? '#16a34a' : '#cbd5e1', cursor: channels.length ? 'pointer' : 'not-allowed'}}>
                {sending ? 'Sending…' : 'Send announcement'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const WaitlistPageWrapper: FC = () => <WaitlistPage />

export {WaitlistPageWrapper}
